import React, { useMemo } from "react";

export type PaginationItem = number | "ellipsis";

/**
 * Builds a compact page list like: 1 2 3 … 48 49 50
 * Merges boundary pages, a window around the current page, and ellipses for gaps.
 */
export function buildPaginationItems(
  currentPage: number,
  totalPages: number,
  siblingCount = 2,
  boundaryCount = 1,
): PaginationItem[] {
  if (totalPages < 1) return [];
  if (totalPages === 1) return [1];

  const safeCurrent = Math.min(Math.max(1, currentPage), totalPages);
  const pages = new Set<number>();

  for (let i = 1; i <= Math.min(boundaryCount, totalPages); i++) {
    pages.add(i);
  }
  for (
    let i = Math.max(1, totalPages - boundaryCount + 1);
    i <= totalPages;
    i++
  ) {
    pages.add(i);
  }
  for (
    let i = safeCurrent - siblingCount;
    i <= safeCurrent + siblingCount;
    i++
  ) {
    if (i >= 1 && i <= totalPages) pages.add(i);
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const out: PaginationItem[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev > 0 && p - prev > 1) out.push("ellipsis");
    out.push(p);
    prev = p;
  }
  return out;
}

export type TablePaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  /** Pages to show on each side of the current page (default 2 → e.g. … 4 5 6 7 8 …) */
  siblingCount?: number;
  /** First / last N pages are always included in the range (default 1 → always 1 and last) */
  boundaryCount?: number;
  className?: string;
  ariaLabel?: string;
};

const TablePagination = ({
  currentPage,
  totalPages,
  onPageChange,
  onPrevious,
  onNext,
  siblingCount = 2,
  boundaryCount = 1,
  className = "",
  ariaLabel = "Table pagination",
}: TablePaginationProps) => {
  const items = useMemo(
    () =>
      buildPaginationItems(
        currentPage,
        totalPages,
        siblingCount,
        boundaryCount,
      ),
    [currentPage, totalPages, siblingCount, boundaryCount],
  );

  if (totalPages < 1) return null;

  const safeCurrent = Math.min(Math.max(1, currentPage), totalPages);
  const canPrev = safeCurrent > 1;
  const canNext = safeCurrent < totalPages;

  const runPrev = () => {
    if (!canPrev) return;
    if (onPrevious) onPrevious();
    else onPageChange(safeCurrent - 1);
  };

  const runNext = () => {
    if (!canNext) return;
    if (onNext) onNext();
    else onPageChange(safeCurrent + 1);
  };

  const ulClass = ["pagination", "justify-content-end", "me-4", className]
    .filter(Boolean)
    .join(" ");

  return (
    <nav aria-label={ariaLabel} className="cursor-pointer">
      <ul className={ulClass}>
        <li className={`page-item ${!canPrev ? "disabled" : ""}`}>
          <button
            type="button"
            className="page-link"
            disabled={!canPrev}
            onClick={runPrev}
          >
            &laquo; Previous
          </button>
        </li>

        {items.map((item, idx) =>
          item === "ellipsis" ? (
            <li key={`e-${idx}`} className="page-item disabled" aria-hidden>
              <span className="page-link">…</span>
            </li>
          ) : (
            <li key={item} className="page-item">
              <button
                type="button"
                className={`page-link ${safeCurrent === item ? "active" : ""}`}
                aria-current={safeCurrent === item ? "page" : undefined}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            </li>
          ),
        )}

        <li className={`page-item ${!canNext ? "disabled" : ""}`}>
          <button
            type="button"
            className="page-link"
            disabled={!canNext}
            onClick={runNext}
          >
            Next &raquo;
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default TablePagination;
